import { useState } from "react";
import { Link } from "react-router-dom";

const footerLinks = [
  {
    title: "Product",
    items: [
      { label: "QR Ordering", href: "#features" },
      { label: "Kitchen Display", href: "#features" },
      { label: "Captain App", href: "#features" },
      { label: "Pricing", href: "#pricing" },
    ],
  },
  {
    title: "Company",
    items: [
      { label: "About Us", href: "#" },
      { label: "Careers", href: "#" },
      { label: "Blog", href: "#" },
      { label: "Press Kit", href: "#" },
    ],
  },
  {
    title: "Support",
    items: [
      { label: "Contact", href: "/contact" },
      { label: "Pricing", href: "#pricing" },
      { label: "Refund & Cancellation", href: "/refund-policy" },
      { label: "Shipping & Delivery", href: "/shipping-policy" },
    ],
  },
  {
    title: "Legal",
    items: [
      { label: "Privacy Policy", href: "/privacy-policy" },
      { label: "Terms & Conditions", href: "/terms" },
      { label: "Refund Policy", href: "/refund-policy" },
      { label: "Contact Us", href: "/contact" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitStatus, setSubmitStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus("loading");

    try {
      const formData = new URLSearchParams();
      formData.append("email", email);

      const response = await fetch(
        "https://readdy.ai/api/form/d8kdpk7jf7p243ot2lhg",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData.toString(),
        }
      );

      if (response.ok) {
        setSubmitStatus("success");
        setEmail("");
      } else {
        setSubmitStatus("error");
      }
    } catch {
      setSubmitStatus("error");
    }
  };

  const handleNavClick = (href: string) => {
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <footer className="bg-foreground-950 text-background-200 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary-500"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-accent-500"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 pt-16 md:pt-20 pb-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
          <div className="col-span-2 md:col-span-1">
            <Link
              to="/"
              className="flex items-center gap-2 text-xl font-heading font-bold text-background-50 mb-4 whitespace-nowrap hover:opacity-80 transition-opacity cursor-pointer"
            >
              <img src="/images/logo.png" alt="EzyDinePro" className="h-9 w-auto" />
            </Link>
            <p className="text-xs text-foreground-400 leading-relaxed mb-5">
              The all-in-one restaurant POS that helps you serve faster, manage
              smarter, and grow bigger.
            </p>
            <div className="flex items-center gap-3">
              {["ri-facebook-fill", "ri-twitter-x-line", "ri-instagram-line", "ri-linkedin-fill"].map(
                (icon) => (
                  <a
                    key={icon}
                    href="#"
                    className="w-9 h-9 flex items-center justify-center rounded-full border border-foreground-700 text-foreground-400 hover:text-background-50 hover:border-background-50 transition-colors cursor-pointer"
                    rel="nofollow"
                  >
                    <i className={`${icon} text-sm`}></i>
                  </a>
                )
              )}
            </div>
          </div>

          {footerLinks.map((group) => (
            <div key={group.title}>
              <h4 className="text-sm font-semibold text-background-50 mb-4">
                <a href="#" className="hover:text-primary-400 transition-colors">
                  {group.title}
                </a>
              </h4>
              <ul className="space-y-2.5">
                {group.items.map((item) => (
                  <li key={item.label}>
                    {item.href.startsWith("/") ? (
                      <Link
                        to={item.href}
                        className="text-xs text-foreground-400 hover:text-background-200 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <a
                        href={item.href}
                        onClick={(e) => {
                          if (item.href.startsWith("#")) {
                            e.preventDefault();
                            handleNavClick(item.href);
                          }
                        }}
                        className="text-xs text-foreground-400 hover:text-background-200 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {item.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-foreground-800 pt-8 pb-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex-1 max-w-md w-full">
              <h4 className="text-sm font-semibold text-background-50 mb-3">
                Stay in the loop
              </h4>
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex gap-2"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="flex-1 px-4 py-2.5 text-sm bg-foreground-900 border border-foreground-700 rounded-md text-background-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <button
                  type="submit"
                  disabled={submitStatus === "loading"}
                  className="px-5 py-2.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70"
                >
                  {submitStatus === "loading" ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    "Subscribe"
                  )}
                </button>
              </form>
              {submitStatus === "success" && (
                <p className="text-xs text-primary-400 mt-2">
                  Thanks for subscribing! Check your inbox.
                </p>
              )}
              {submitStatus === "error" && (
                <p className="text-xs text-accent-400 mt-2">
                  Something went wrong. Please try again.
                </p>
              )}
            </div>

            <div className="flex items-center gap-6 text-xs text-foreground-500">
              <span>&copy; {new Date().getFullYear()} EzyDinePro. All rights reserved.</span>
              <Link to="/privacy-policy" className="hover:text-background-200 transition-colors whitespace-nowrap cursor-pointer">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-background-200 transition-colors whitespace-nowrap cursor-pointer">
                Terms
              </Link>
              <Link to="/contact" className="hover:text-background-200 transition-colors whitespace-nowrap cursor-pointer">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}